<template>
    <div @click.self="$emit('close')" class="modal">
        <!-- Modal: useDialog moves focus in, keeps Tab inside, closes on Escape
             and hands focus back to the Share button. -->
        <div ref="dialogEl" class="modal-content" role="dialog" aria-modal="true" :aria-labelledby="titleId" tabindex="-1">
            <button type="button" @click="$emit('close')" class="close" aria-label="Close"><span aria-hidden="true">&times;</span></button>
            <h4 :id="titleId" class="headline-4">Share</h4>
            <!-- Each target is one link. They were links wrapping buttons, which
                 is invalid (interactive content inside interactive content) and
                 put two tab stops on every target. -->
            <div class="target">
                <a :href="'https://www.facebook.com/sharer/sharer.php?u=' + encodeUrl" target="_blank" rel="noopener">
                    <SvgsShareFacebook />
                    <span>Facebook</span>
                </a>

                <a :href="'https://www.twitter.com/share?url=' + encodeUrl" target="_blank" rel="noopener">
                    <SvgsShareTwitter />
                    <span>Twitter</span>
                </a>

                <a :href="'https://www.linkedin.com/sharing/share-offsite/?url=' + encodeUrl" target="_blank" rel="noopener">
                    <SvgsShareLinkedin />
                    <span>LinkedIn</span>
                </a>

                <a :href="'mailto:?body=' + encodeUrl" target="_blank" rel="noopener">
                    <SvgsEmail />
                    <span>Email</span>
                </a>
            </div>

            <div class="link">
                <span class="url">{{ url }}</span>
                <!-- One button whose label reports the result, instead of three
                     swapped in and out; the result is also spoken through the
                     status region, since a label change alone isn't announced. -->
                <button type="button" @click="copyLink(localUrl)" class="copy-link">
                    <template v-if="linkCopied">Copied <span aria-hidden="true">&check;</span></template>
                    <template v-else-if="copyError">Couldn't copy</template>
                    <template v-else>Copy</template>
                </button>
                <span class="sr-only" role="status">{{ copyStatus }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
    const props = defineProps({
        url: {
            type: String,
            required: false,
        },
        navobj: {
            type: Object,
            require: false,
        },
    });
    const emit = defineEmits(['close']);
    const linkCopied = ref(false);
    const copyError = ref(false);
    const copyStatus = computed(() => {
        if (linkCopied.value) return 'Link copied';
        if (copyError.value) return "Couldn't copy the link";
        return '';
    });

    const dialogEl = ref(null);
    const titleId = useId();
    useDialog(dialogEl, { onEscape: () => emit('close') });

    const localUrl = computed(() => {
        if (props.navobj?.url) return props.navobj?.url;
        return props.url;
    });

    const encodeUrl = computed(() => {
        return encodeURIComponent(localUrl.value);
    });

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            linkCopied.value = true;
        } catch (err) {
            console.error(err);
            copyError.value = true;
        }
        setTimeout(() => {
            copyError.value = false;
            linkCopied.value = false;
        }, 2000);
    };
</script>

<style lang="scss" scoped>
    .modal-content:focus {
        outline: none;
    }

    .modal {
        position: fixed;
        z-index: 100;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.5);

        .modal-content {
            padding: 20px;
            width: 80%;
            background-color: $white;
            border-radius: 4px;
            border: 1px solid $gray-light;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);

            .headline-4 {
                text-align: left;
                padding: 0 0 10px 0;
            }

            a,
            .link button,
            button {
                // border: 1px solid $gray-mid;
                border-radius: 4px;
                display: flex;
                justify-content: center;
                align-items: flex-end;
                gap: 5px;
                padding: 10px 0;

                &:hover,
                &:focus {
                    background-color: $gray-light;
                }
            }

            a,
            .link button {
                border: 1px solid $gray-mid;
            }

            .target {
                display: flex;
                flex-wrap: wrap;
                gap: 20px;
                padding: 0 0 20px 0;

                a {
                    flex-basis: calc(50% - 10px);
                    // 20px, not the shared 10px: these used to be a padded
                    // button inside a padded link, and are the same size now
                    // the button is gone.
                    padding: 20px 0;

                    svg {
                        height: 20px;
                    }
                }
            }

            .link {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 10px;
                border-radius: 4px;
                background-color: #eee;

                .url {
                    margin-right: 15px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .copy-link {
                    padding: 10px;
                    white-space: nowrap;
                    line-height: 1;
                }
            }

            // A <button> now (was a <span>). Undo the shared button styling
            // above so it still looks like the bare × it was.
            .close {
                display: block;
                color: #aaa;
                float: right;
                font-size: 28px;
                font-weight: bold;
                padding: 0;
                border-radius: 0;

                &:hover,
                &:focus {
                    color: black;
                    background-color: transparent;
                    text-decoration: none;
                    cursor: pointer;
                }
            }
        }
    }
</style>
